import { useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import Button from "shared/Button";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";
import Card from "shared/Card";
import { Section } from "shared/layout";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import FileTypeIcon from "../../components/FileTypeIcon";

import styles from "./FileList.module.css";

export function FileList({
  files,
  stagedFile,
  onClearStagedFile,
  onArchiveFile,
  onUnarchiveFile,
  onRemoveArchived,
}) {
  const [showArchived, setShowArchived] = useState(false);

  const [stagedToArchive, setStagedToArchive] = useState(null);
  const [stagedToUnArchive, setStagedToUnArchive] = useState(null);

  const filteredFiles = files
    .filter((file) => !file.deleted && (showArchived || !file.archived))
    .sort(
      (left, right) =>
        Number(Boolean(left.archived)) - Number(Boolean(right.archived))
    );

  const isEmpty = files.every((file) => file.deleted) && stagedFile == null;

  return (
    <Card>
      <Section>
        <header>
          <h3>Files</h3>

          <div className={styles.filesCheckboxes}>
            <div className={styles.checkbox}>
              <CheckboxGroup
                id="filesIncludeArchived"
                checked={showArchived}
                disabled={isEmpty}
                onClick={() => {
                  setShowArchived(!showArchived);
                  // console.warn("Not implemented yet.");
                }}
              >
                Show Archived
              </CheckboxGroup>
            </div>
            {/* <div className={styles.checkbox}>
              <CheckboxGroup
                id="filesIncludeDeleted"
                onClick={() => {
                  console.warn("Not implemented yet.");
                }}
              >
                Deleted
              </CheckboxGroup>
            </div> */}
          </div>
        </header>

        {isEmpty ? (
          <span className={styles.empty}>No files added</span>
        ) : (
          <ul className={styles.list}>
            {stagedFile && (
              <li className={styles.listItem}>
                <FileTypeIcon
                  fileTypeId={stagedFile.fileFormat}
                  className={styles.icon}
                />

                <div className={styles.metadata}>
                  <div className={styles.fileName}>
                    {stagedFile.file ? stagedFile.file.name : "New File"}

                    <span className={styles.uploadBadge}>Staged</span>
                  </div>

                  <div className={styles.secondaryInfo}>
                    {stagedFile.version && (
                      <div className={styles.version}>
                        v{stagedFile.version.toFixed(1)}
                      </div>
                    )}

                    <div className={styles.saveNotice}>
                      Will be uploaded when saved
                    </div>
                  </div>
                </div>

                <div className={styles.controls}>
                  <Button
                    purpose="delete"
                    size="small"
                    onClick={onClearStagedFile}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            )}

            {filteredFiles.map((file) => {
              return (
                <li key={file.fileId} className={styles.listItem}>
                  <FileTypeIcon
                    fileTypeId={file.fileFormat}
                    className={styles.icon}
                  />

                  <div className={styles.metadata}>
                    <div className={styles.fileName}>
                      {file.originalFileName || file.filePath}
                    </div>

                    <div className={styles.secondaryInfo}>
                      {file.version && (
                        <div className={styles.version}>
                          v{file.version.toFixed(1)}
                        </div>
                      )}

                      <div className={styles.date}>
                        {dayjs(file.uploadedOn).format("M/D/YYYY")}
                      </div>

                      {file.archived && (
                        <span className={styles.archived}>(Archived)</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.buttons}>
                    {file.archived ? (
                      <>
                        <Button
                          size="small"
                          onClick={() => onUnarchiveFile(file)}
                        >
                          Unarchive
                        </Button>
                        <Button
                          purpose="delete"
                          size="small"
                          onClick={() => onRemoveArchived(file)}
                        >
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => {
                          onArchiveFile(file);
                        }}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Modal
        title="Confirm Archive?"
        isOpen={stagedToArchive != null}
        onClose={() => {
          setStagedToArchive(null);
        }}
      >
        <p>Are you sure you want to archive this item?</p>

        <ModalActionBar>
          <Button
            onClick={() => {
              setStagedToArchive(null);
            }}
          >
            Cancel
          </Button>
          <Button
            purpose="delete"
            onClick={() => {
              onArchiveFile(stagedToArchive);
              setStagedToArchive(null);
            }}
          >
            Archive
          </Button>
        </ModalActionBar>
      </Modal>

      <Modal
        title="Confirm Un-Archive?"
        isOpen={stagedToUnArchive != null}
        onClose={() => {
          setStagedToUnArchive(null);
        }}
      >
        <p>Are you sure you want to un-archive this item?</p>

        <ModalActionBar>
          <Button
            onClick={() => {
              setStagedToUnArchive(null);
            }}
          >
            Cancel
          </Button>
          <Button
            purpose="delete"
            onClick={() => {
              onUnarchiveFile(stagedToUnArchive);
              setStagedToUnArchive(null);
            }}
          >
            Un-Archive
          </Button>
        </ModalActionBar>
      </Modal>
    </Card>
  );
}

FileList.propTypes = {
  /**
   * List of files to display.
   */
  files: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.number.isRequired,
      fileFormat: PropTypes.number.isRequired,
      version: PropTypes.number,
      originalFileName: PropTypes.string.isRequired,
      uploadedOn: PropTypes.string.isRequired, // timestamp as ISO string from API
      deleted: PropTypes.bool,
      archived: PropTypes.bool,
    })
  ).isRequired,

  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),

  /**
   * Called when the file's status is changed. Takes the file and the new status as arguments.
   */
  onStatusChange: PropTypes.func,

  /**
   * Called when the user confirms archiving of a file. Takes the file object as an argument.
   */
  onArchiveFile: PropTypes.func,

  /**
   * Called when the user confirms un-archiving of a file. Takes the file object as an argument.
   */
  onUnarchiveFile: PropTypes.func,

  /**
   * Called when a previously archived file is permanently removed.
   */
  onRemoveArchived: PropTypes.func,
};
